// @ts-nocheck
import * as THREE from 'three';
import type { SceneProps } from '../sceneTypes';
import { musicalPulse, seededUnit } from '../sceneUtils';

function motionSpeedFor(tune: Record<string, number>, intensity = 0, cap = 0.66) {
  return Math.min(tune.speed ?? 1, cap + Math.min(1, intensity) * 0.08);
}

function motionCap(value: number, cap = 0.72) {
  return THREE.MathUtils.clamp(value, 0, cap);
}

function continuousBeat(beat: SceneProps['beat']) {
  return beat.beatIndex + beat.beatPhase;
}

type CathedralRefs = {
  root: React.RefObject<THREE.Group>;
  nave: React.RefObject<THREE.InstancedMesh>;
  roofRib: React.RefObject<THREE.InstancedMesh>;
  buttress: React.RefObject<THREE.InstancedMesh>;
  spire: React.RefObject<THREE.InstancedMesh>;
  roseWindow: React.RefObject<THREE.InstancedMesh>;
  column: React.RefObject<THREE.InstancedMesh>;
  arch: React.RefObject<THREE.InstancedMesh>;
  rail: React.RefObject<THREE.InstancedMesh>;
  floor: React.RefObject<THREE.InstancedMesh>;
  beam: React.RefObject<THREE.InstancedMesh>;
  bridge: React.RefObject<THREE.InstancedMesh>;
  portal: React.RefObject<THREE.InstancedMesh>;
  window: React.RefObject<THREE.InstancedMesh>;
  tree: React.RefObject<THREE.InstancedMesh>;
  monument: React.RefObject<THREE.InstancedMesh>;
  particle: React.RefObject<THREE.Points>;
  waterfall: React.RefObject<THREE.Points>;
};

type CathedralProcessorConfig = {
  depth: number;
  railCount: number;
  railSegments: number;
  floorCount: number;
};

type CathedralProcessorFrame = {
  state: any;
  delta: number;
  props: SceneProps;
  tune: Record<string, number>;
  journey: Record<string, number | string>;
  stage: number;
  refs: CathedralRefs;
  data: Record<string, any>;
};

export function createCathedral3DProcessor({
  depth,
  railCount,
  railSegments,
  floorCount,
}: CathedralProcessorConfig) {
  const dummy = new THREE.Object3D();
  let travel = 0;
  let beatLift = 0;
  let shockValue = 0;

  return {
    update({
      state,
      delta,
      props,
      tune,
      journey,
      stage,
      refs,
      data,
    }: CathedralProcessorFrame) {
      const { beat, intensity, section } = props;
      const t = state.clock.elapsedTime;
      const music = musicalPulse(props);
      const beatTarget = Math.max(music.groove * 0.45, music.impact, music.phrase * 0.8);
      beatLift = THREE.MathUtils.lerp(beatLift, beatTarget, 1 - Math.pow(0.0006, delta));
      const pulse = beatLift * tune.flash;
      const dropPulse = section === 'drop' ? pulse : 0;
      shockValue = THREE.MathUtils.lerp(shockValue, dropPulse, 1 - Math.pow(0.0005, delta));
      const shock = shockValue;
      const motionSpeed = motionSpeedFor(tune, intensity);
      const motionShock = motionCap(shock, 0.5);
      const beatClock = continuousBeat(beat);
      const chamberWave = Math.sin(journey.progress * Math.PI * 2);
      const speed = (0.42 + stage * 0.55 + journey.peak * 0.16 + motionShock * 0.18) * motionSpeed;
      travel = (travel + delta * speed * 1.25) % depth;
      const chamberEvent = Math.max(0, Math.sin(journey.progress * Math.PI * 4 - 0.35));
      const rareEvent = THREE.MathUtils.smoothstep(chamberEvent, 0.58, 0.98);

      const chamberWidthAt = (raw: number) => {
        const hall = Math.sin(raw * 0.074 + journey.turn * 1.6) * 0.5 + 0.5;
        const chamber = Math.pow(hall, 1.75);
        return (2.6 + chamber * 2.6 + journey.reveal * 0.5 + stage * 0.45 + shock * 1.1) * tune.depth;
      };
      const heightAt = (raw: number) => (
        2.4 + Math.sin(raw * 0.053 + journey.turn) * 0.4 + stage * 0.65 + shock * 0.8
      ) * tune.depth;
      // Do not shrink objects as they pass the camera. Perspective and clipping
      // should make architecture leave view, while far objects can still ease in.
      const fadeFor = (raw: number) =>
        1 - THREE.MathUtils.smoothstep(raw, depth - 20, depth - 2);

      if (refs.nave.current) {
        data.navePieces.forEach((piece, i) => {
          const raw = (piece.raw - travel + depth) % depth;
          const fade = fadeFor(raw);
          const width = chamberWidthAt(raw);
          const chamberScale = 0.78 + Math.sin(raw * 0.074 + journey.turn * 1.6) * 0.08 + rareEvent * 0.14;
          const side = piece.side || 0;
          const bayDepth = data.modelBayDepth * 0.58;
          if (piece.kind === 'floor') {
            dummy.position.set(0, -2.72, 4.3 - raw);
            dummy.rotation.set(0, 0, 0);
            dummy.scale.set(width * 0.9 * fade, 0.08 * fade, bayDepth * fade);
          } else if (piece.kind === 'aisle-floor') {
            dummy.position.set(side * width * 0.86, -2.62, 4.3 - raw);
            dummy.rotation.set(0, 0, 0);
            dummy.scale.set(width * 0.18 * fade, 0.05 * fade, bayDepth * 0.92 * fade);
          } else if (piece.kind === 'wall') {
            dummy.position.set(side * width * 0.78, -0.62, 4.3 - raw);
            dummy.rotation.set(0, 0, side * 0.02);
            dummy.scale.set(0.16 * fade, (4.2 + stage * 0.8 + rareEvent * 1.2) * chamberScale * fade, bayDepth * fade);
          } else if (piece.kind === 'clerestory') {
            dummy.position.set(side * width * 0.82, 1.84, 4.3 - raw);
            dummy.rotation.set(0, 0, side * -0.12);
            dummy.scale.set(0.08 * fade, (1.55 + stage * 0.5) * fade, bayDepth * 0.72 * fade);
          } else {
            dummy.position.set(side * width * 0.9, 0.45, 4.3 - raw);
            dummy.rotation.set(0, 0, side * 0.18);
            dummy.scale.set(0.08 * fade, 2.3 * fade, bayDepth * 0.55 * fade);
          }
          dummy.updateMatrix();
          refs.nave.current.setMatrixAt(i, dummy.matrix);
        });
        refs.nave.current.instanceMatrix.needsUpdate = true;
        refs.nave.current.material.opacity = 0.08 + stage * 0.035 + rareEvent * 0.025;
      }

      if (refs.roofRib.current) {
        data.roofRibs.forEach((rib, i) => {
          const raw = (rib.raw - travel + depth) % depth;
          const fade = fadeFor(raw);
          const width = chamberWidthAt(raw);
          const ribLength = 2.1 + width * 0.18;
          const side = rib.side || 1;
          if (rib.kind === 'diagonal') {
            dummy.position.set(side * width * 0.68, 1.72 + rib.level * 0.18, 4.15 - raw);
            dummy.rotation.set(0, 0, side * -0.34);
            dummy.scale.set(0.035 * fade, ribLength * 0.52 * fade, 0.035 * fade);
          } else if (rib.kind === 'arch') {
            dummy.position.set(side * width * 0.78, 0.7 + rib.level * 0.2, 4.15 - raw);
            dummy.rotation.set(0, 0, side * 0.18);
            dummy.scale.set(0.03 * fade, 1.75 * fade, 0.03 * fade);
          } else {
            dummy.position.set(0, 2.55 + stage * 0.22, 4.15 - raw);
            dummy.rotation.set(Math.PI / 2, 0, 0);
            dummy.scale.set(0.04 * fade, 0.04 * fade, data.modelBayDepth * 0.7 * fade);
          }
          dummy.updateMatrix();
          refs.roofRib.current.setMatrixAt(i, dummy.matrix);
        });
        refs.roofRib.current.instanceMatrix.needsUpdate = true;
        refs.roofRib.current.material.opacity = 0.13 + stage * 0.08 + shock * 0.05;
      }

      if (refs.buttress.current) {
        data.buttresses.forEach((buttress, i) => {
          const raw = (buttress.raw - travel * 0.68 + depth) % depth;
          const fade = fadeFor(raw);
          const width = chamberWidthAt(raw);
          const side = buttress.side;
          if (buttress.kind === 'flying') {
            dummy.position.set(side * width * 0.78, -0.2 + buttress.level * 0.65, 4.0 - raw);
            dummy.rotation.set(0, 0, side * -0.62);
            dummy.scale.set(0.075 * fade, (2.3 + stage * 0.4) * fade, 0.075 * fade);
          } else {
            dummy.position.set(side * width * 0.96, -1.45, 4.0 - raw);
            dummy.rotation.set(0, 0, side * 0.04);
            dummy.scale.set(0.16 * fade, 2.5 * fade, 0.18 * fade);
          }
          dummy.updateMatrix();
          refs.buttress.current.setMatrixAt(i, dummy.matrix);
        });
        refs.buttress.current.instanceMatrix.needsUpdate = true;
        refs.buttress.current.material.opacity = 0.07 + stage * 0.05 + rareEvent * 0.025;
      }

      if (refs.spire.current) {
        data.spires.forEach((spire, i) => {
          const raw = (spire.raw - travel * 0.64 + depth) % depth;
          const fade = fadeFor(raw);
          const width = chamberWidthAt(raw);
          dummy.position.set(spire.side * width * spire.lane, 1.8 + spire.height * 0.5, 3.9 - raw);
          dummy.rotation.set(0, spire.side * 0.08, 0);
          dummy.scale.set(spire.radius * fade, spire.height * (1 + rareEvent * 0.18) * fade, spire.radius * fade);
          dummy.updateMatrix();
          refs.spire.current.setMatrixAt(i, dummy.matrix);
        });
        refs.spire.current.instanceMatrix.needsUpdate = true;
        refs.spire.current.material.opacity = 0.12 + stage * 0.07 + shock * 0.06;
      }

      if (refs.roseWindow.current) {
        data.roseWindows.forEach((rose, i) => {
          const raw = (rose.raw - travel * 0.56 + depth) % depth;
          const fade = fadeFor(raw);
          const scale = rose.scale * (1 + rareEvent * 0.18 + shock * 0.12);
          dummy.position.set(0, 0.72 + rose.y, 3.65 - raw);
          dummy.rotation.set(0, 0, rose.spin + t * 0.018);
          dummy.scale.set(scale * fade, scale * fade, scale * fade);
          dummy.updateMatrix();
          refs.roseWindow.current.setMatrixAt(i, dummy.matrix);
        });
        refs.roseWindow.current.instanceMatrix.needsUpdate = true;
        refs.roseWindow.current.material.opacity = 0.11 + rareEvent * 0.1 + stage * 0.05;
      }

      if (refs.column.current) {
        data.columns.forEach((column, i) => {
          const raw = (column.raw - travel + depth) % depth;
          const fade = fadeFor(raw);
          const width = chamberWidthAt(raw);
          const h = (column.height + heightAt(raw)) * (0.9 + stage * 0.12 + shock * 0.15);
          const rise = Math.max(0, Math.sin(journey.progress * Math.PI * 2 + column.lane * 0.48));
          dummy.position.set(
            column.side * (width + Math.sin(t * 0.15 + column.phase) * 0.08),
            -2.3 + h / 2 + rise * journey.reveal * 0.26,
            4.5 - raw
          );
          dummy.rotation.set(0, column.side * 0.08 + chamberWave * 0.02, column.side * 0.02);
          dummy.scale.set(column.thickness * fade, h * fade, (0.15 + stage * 0.04) * fade);
          dummy.updateMatrix();
          refs.column.current.setMatrixAt(i, dummy.matrix);
        });
        refs.column.current.instanceMatrix.needsUpdate = true;
        refs.column.current.material.opacity = 0.22 + stage * 0.1 + shock * 0.08;
      }

      if (refs.arch.current) {
        data.arches.forEach((arch, i) => {
          const raw = (arch.raw - travel + depth) % depth;
          const fade = fadeFor(raw);
          const width = chamberWidthAt(raw) * arch.scale;
          const height = heightAt(raw) * 1.25 * arch.scale;
          dummy.position.set(0, -0.4 + height * 0.1, 4.2 - raw);
          dummy.rotation.set(
            Math.sin(raw * 0.026 + journey.turn) * 0.05,
            Math.cos(raw * 0.021 + journey.turn) * 0.08,
            arch.rotation + t * arch.spin * 0.04
          );
          dummy.scale.set(width * fade, height * fade, 0.6 * fade);
          dummy.updateMatrix();
          refs.arch.current.setMatrixAt(i, dummy.matrix);
        });
        refs.arch.current.instanceMatrix.needsUpdate = true;
        refs.arch.current.material.opacity = 0.1 + intensity * 0.045 + stage * 0.05 + rareEvent * 0.05 + shock * 0.07;
      }

      if (refs.rail.current) {
        let instance = 0;
        for (let rail = 0; rail < railCount; rail += 1) {
          const a = (rail / railCount) * Math.PI * 2;
          for (let segment = 0; segment < railSegments; segment += 1) {
            const raw = ((segment / railSegments) * depth - travel + depth) % depth;
            const fade = fadeFor(raw);
            const width = chamberWidthAt(raw) * (0.78 + Math.sin(rail * 1.7) * 0.08);
            const height = heightAt(raw) * 0.58;
            const railWave = Math.sin(raw * 0.18 - beatClock * 0.42 + rail) * pulse * 0.055;
            dummy.position.set(
              Math.cos(a + railWave) * width,
              Math.sin(a + railWave) * height - 0.15,
              4.2 - raw
            );
            dummy.rotation.set(0, 0, a + Math.PI / 2);
            dummy.scale.set(0.024 * fade, 0.024 * fade, (depth / railSegments) * 0.58 * fade);
            dummy.updateMatrix();
            refs.rail.current.setMatrixAt(instance, dummy.matrix);
            instance += 1;
          }
        }
        refs.rail.current.instanceMatrix.needsUpdate = true;
        refs.rail.current.material.opacity = 0.18 + intensity * 0.08 + stage * 0.08 + shock * 0.09;
      }

      if (refs.floor.current) {
        data.floorSlabs.forEach((slab, i) => {
          const raw = (slab.raw - travel + depth) % depth;
          const fade = fadeFor(raw);
          const width = chamberWidthAt(raw) * (slab.split ? 0.76 : 1.08);
          dummy.position.set(0, -2.52, 4.1 - raw);
          dummy.rotation.set(-Math.PI / 2, 0, 0);
          dummy.scale.set(width * fade, (depth / floorCount) * 0.56 * slab.glow * fade, 1);
          dummy.updateMatrix();
          refs.floor.current.setMatrixAt(i, dummy.matrix);
        });
        refs.floor.current.instanceMatrix.needsUpdate = true;
        refs.floor.current.material.opacity = 0.04 + stage * 0.026 + shock * 0.025;
      }

      if (refs.beam.current) {
        data.beams.forEach((beam, i) => {
          const raw = (beam.raw - travel * 0.5 + depth) % depth;
          const fade = fadeFor(raw);
          const width = chamberWidthAt(raw);
          const active = 0.16 + Math.max(0, stage - 0.42) * 1.1 + rareEvent * 0.45 + shock * 1.2;
          dummy.position.set(beam.side * width * beam.lane, -2.7 + beam.height / 2, 4 - raw);
          dummy.rotation.set(0, 0, Math.sin(t * 0.12 + i) * 0.025);
          dummy.scale.set(beam.thickness * active * fade, beam.height * (0.5 + active) * fade, beam.thickness * active * fade);
          dummy.updateMatrix();
          refs.beam.current.setMatrixAt(i, dummy.matrix);
        });
        refs.beam.current.instanceMatrix.needsUpdate = true;
        refs.beam.current.material.opacity = 0.07 + stage * 0.11 + rareEvent * 0.1 + shock * 0.12;
      }

      if (refs.bridge.current) {
        data.bridges.forEach((bridge, i) => {
          const raw = (bridge.raw - travel * 0.58 + depth) % depth;
          const fade = fadeFor(raw);
          const width = chamberWidthAt(raw);
          dummy.position.set(0, bridge.y + Math.sin(t * 0.2 + bridge.angle) * 0.08, 4 - raw);
          dummy.rotation.set(0, 0, bridge.angle + Math.sin(journey.turn + i) * 0.08);
          dummy.scale.set(width * bridge.width * fade, 0.025 * fade, 0.04 * fade);
          dummy.updateMatrix();
          refs.bridge.current.setMatrixAt(i, dummy.matrix);
        });
        refs.bridge.current.instanceMatrix.needsUpdate = true;
        refs.bridge.current.material.opacity = 0.1 + stage * 0.07 + shock * 0.06;
      }

      if (refs.portal.current) {
        data.portals.forEach((portal, i) => {
          const raw = (portal.raw - travel * (0.52 + stage * 0.08) + depth) % depth;
          const fade = fadeFor(raw);
          const width = chamberWidthAt(raw) * 0.52 * portal.scale * (1 + shock * 0.22);
          dummy.position.set(0, 0.15, 3.8 - raw);
          dummy.rotation.set(0, 0, portal.spin + t * (0.04 + stage * 0.02) + i * 0.09);
          dummy.scale.set(width * fade, width * 1.35 * fade, width * fade);
          dummy.updateMatrix();
          refs.portal.current.setMatrixAt(i, dummy.matrix);
        });
        refs.portal.current.instanceMatrix.needsUpdate = true;
        refs.portal.current.material.opacity = 0.035 + stage * 0.035 + rareEvent * 0.13 + shock * 0.16;
      }

      if (refs.window.current) {
        data.windows.forEach((window, i) => {
          const raw = (window.raw - travel * 0.4 + depth) % depth;
          const fade = fadeFor(raw);
          const width = chamberWidthAt(raw);
          const openness = 0.45 + rareEvent * 0.8 + (section === 'chorus' ? 0.25 : 0);
          dummy.position.set(window.side * width * 1.12, window.y, 3.6 - raw);
          dummy.rotation.set(0, window.side * Math.PI / 2, window.spin + t * 0.035);
          dummy.scale.set(window.size * openness * fade, window.size * 1.7 * openness * fade, 1);
          dummy.updateMatrix();
          refs.window.current.setMatrixAt(i, dummy.matrix);
        });
        refs.window.current.instanceMatrix.needsUpdate = true;
        refs.window.current.material.opacity = 0.05 + rareEvent * 0.15 + stage * 0.03;
      }

      if (refs.tree.current) {
        data.treeBranches.forEach((branch, i) => {
          const raw = (branch.raw - travel * 0.44 + depth) % depth;
          const fade = fadeFor(raw);
          const width = chamberWidthAt(raw);
          const branchLevel = branch.branch % 4;
          const sideOffset = branch.side * width * (0.62 + branchLevel * 0.05);
          const height = branch.height * (0.95 + stage * 0.26 + rareEvent * 0.4);
          dummy.position.set(
            sideOffset + branch.side * branchLevel * 0.16,
            -2.25 + branchLevel * 0.42 + height / 2,
            3.7 - raw
          );
          dummy.rotation.set(0, 0, branch.side * (0.16 + branchLevel * 0.22) + branch.lean);
          dummy.scale.set(0.018 * fade, height * fade, 0.018 * fade);
          dummy.updateMatrix();
          refs.tree.current.setMatrixAt(i, dummy.matrix);
        });
        refs.tree.current.instanceMatrix.needsUpdate = true;
        refs.tree.current.material.opacity = 0.08 + stage * 0.075 + rareEvent * 0.1;
      }

      if (refs.monument.current) {
        data.monuments.forEach((monument, i) => {
          const raw = (monument.raw - travel * 0.3 + depth) % depth;
          const fade = fadeFor(raw);
          const width = chamberWidthAt(raw) * (0.55 + seededUnit(i, 19) * 0.32);
          dummy.position.set(
            monument.side * width + Math.sin(t * 0.13 + monument.drift) * 0.18,
            monument.height + Math.sin(t * 0.17 + monument.drift) * 0.2 + shock * 0.4,
            3.4 - raw
          );
          dummy.rotation.set(t * 0.09 + monument.drift, monument.drift + t * 0.13, t * 0.07);
          dummy.scale.setScalar(monument.size * (0.8 + stage * 0.45 + shock * 0.8) * fade);
          dummy.updateMatrix();
          refs.monument.current.setMatrixAt(i, dummy.matrix);
        });
        refs.monument.current.instanceMatrix.needsUpdate = true;
        refs.monument.current.material.opacity = 0.12 + stage * 0.1 + shock * 0.1;
      }

      const positions = data.particlePositions;
      if (positions && refs.particle.current) {
        data.particleSeeds.forEach((particle, i) => {
          const raw = (particle.raw - travel * particle.speed * (0.48 + stage * 0.2 + motionShock * 0.18) + depth) % depth;
          const width = chamberWidthAt(raw);
          const height = heightAt(raw);
          const near = 1 - raw / depth;
          const px = particle.x / 6;
          const py = particle.y / 4;
          const radius = Math.hypot(px, py * 0.8);
          const corridor = 0.44;
          const corridorScale = radius < corridor ? corridor / Math.max(0.08, radius) : 1;
          positions[i * 3] = px * corridorScale * width + Math.sin(t * 0.19 + particle.drift) * 0.18;
          positions[i * 3 + 1] = py * corridorScale * height + Math.cos(t * 0.13 + particle.drift) * 0.14;
          positions[i * 3 + 2] = 4.4 - raw + near * particle.speed * (0.55 + stage * 0.45);
        });
        refs.particle.current.geometry.attributes.position.needsUpdate = true;
        refs.particle.current.material.opacity = 0.18 + stage * 0.08 + shock * 0.09;
        refs.particle.current.material.size = 0.018 + stage * 0.009 + shock * 0.012;
      }

      const waterfallPositions = data.waterfallPositions;
      if (waterfallPositions && refs.waterfall.current) {
        data.waterfallSeeds.forEach((fall, i) => {
          const raw = (fall.raw - travel * 0.34 + depth) % depth;
          const width = chamberWidthAt(raw);
          const fallPhase = (fall.y + t * fall.speed * (0.08 + rareEvent * 0.18 + stage * 0.06)) % 1;
          const curtainWidth = fall.side === 0 ? width * 0.62 : width * 0.22;
          waterfallPositions[i * 3] = fall.side * width * 0.62 + fall.x * curtainWidth;
          waterfallPositions[i * 3 + 1] = 4.2 - fallPhase * (5.5 + stage * 1.2);
          waterfallPositions[i * 3 + 2] = 3.4 - raw;
        });
        refs.waterfall.current.geometry.attributes.position.needsUpdate = true;
        refs.waterfall.current.material.opacity = 0.015 + rareEvent * 0.14 + shock * 0.045;
        refs.waterfall.current.material.size = 0.015 + rareEvent * 0.009;
      }

      if (refs.root.current) {
        refs.root.current.position.set(
          Math.sin(t * 0.1 * motionSpeed + journey.turn) * (0.08 + stage * 0.07),
          Math.cos(t * 0.08 * motionSpeed + journey.turn * 0.6) * (0.06 + stage * 0.05),
          0
        );
        refs.root.current.rotation.z = Math.sin(t * 0.055 * motionSpeed + journey.turn) * (0.02 + stage * 0.035 + motionShock * 0.025);
        refs.root.current.rotation.y = Math.sin(journey.turn * 0.5) * 0.035;
        refs.root.current.scale.setScalar(0.9 + journey.reveal * 0.08 + shock * 0.06);
      }

      if (state.camera && 'fov' in state.camera) {
        const targetFov = section === 'drop'
          ? 72 + shock * 7
          : section === 'build'
            ? 57 - journey.chapterProgress * 2.5
            : 62 + stage * 5;
        state.camera.fov = THREE.MathUtils.lerp(state.camera.fov, targetFov, 0.016);
        state.camera.updateProjectionMatrix();
      }
    },
  };
}
